import React from 'react';
import { Card, CardHeader, CardBody, Text, Flex, Select } from '@backstage/ui';
import { useApi, appThemeApiRef } from '@backstage/core-plugin-api';
import { appLanguageApiRef, useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { RiPaletteLine, RiTranslate2, RiContrastLine } from '@remixicon/react';
import { appTranslationRef } from '../../../../core/i18n';
import styles from '../../settings.module.css';

const languageLabels: Record<string, string> = {
  en: 'English',
  fr: 'Français',
};

export const AppearanceCard = () => {
  const { t } = useTranslationRef(appTranslationRef);
  const languageApi = useApi(appLanguageApiRef);
  const themeApi = useApi(appThemeApiRef);

  const [currentLanguage, setCurrentLanguage] = React.useState(
    languageApi.getLanguage().language,
  );

  const availableLanguages = languageApi.getAvailableLanguages().languages;
  const themes = themeApi.getInstalledThemes();
  const activeThemeId = themeApi.getActiveThemeId();

  React.useEffect(() => {
    const subscription = languageApi.language$().subscribe(({ language }) => {
      setCurrentLanguage(language);
    });
    return () => subscription.unsubscribe();
  }, [languageApi]);

  const languageOptions = availableLanguages.map(lang => ({
    value: lang,
    label: languageLabels[lang] ?? lang,
  }));

  const themeOptions = themes.map(theme => ({
    value: theme.id,
    label: theme.title,
  }));

  return (
    <Card>
      <CardHeader>
        <Flex gap="3" align="center">
          <RiPaletteLine size={18} className={styles.cardIcon} />
          <Text variant="title-small" weight="bold">
            {t('settings.appearance.title')}
          </Text>
        </Flex>
      </CardHeader>
      <CardBody>
        <Flex direction="column" gap="3">
          <div className={styles.settingRow}>
            <div className={styles.settingLabel}>
              <RiTranslate2 size={16} className={styles.settingLabelIcon} />
              <Text variant="body-small" weight="bold">
                {t('settings.appearance.language')}
              </Text>
            </div>
            <Select
              aria-label={t('settings.appearance.language')}
              options={languageOptions}
              defaultValue={currentLanguage}
              onChange={value => {
                languageApi.setLanguage(value as string);
              }}
            />
          </div>
          <div className={styles.settingRow}>
            <div className={styles.settingLabel}>
              <RiContrastLine size={16} className={styles.settingLabelIcon} />
              <Text variant="body-small" weight="bold">
                {t('settings.appearance.theme')}
              </Text>
            </div>
            <Select
              aria-label={t('settings.appearance.theme')}
              options={themeOptions}
              defaultValue={activeThemeId ?? themes[0]?.id ?? ''}
              onChange={value => {
                themeApi.setActiveThemeId(value as string);
              }}
            />
          </div>
        </Flex>
      </CardBody>
    </Card>
  );
};
